import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { App, Spin, Typography, Upload, UploadFile, UploadProps } from 'antd';
import { sanitizeFileName, sanitizeUrl } from 'src/utils/sanitize';

import { CopyOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';

import {
  ButtonsWrapper,
  DisplayNameWrapper,
  FileDescription,
  FileNameTextContent,
  FileNameTextWrapper,
  FilePreview,
  IconWrapper,
  InboxIcon,
  LoadingWrapper,
  PreviewButton,
  PreviewContainer,
  PreviewContent,
  PreviewContentWrapper,
  PreviewImage,
  PreviewImageContainer,
  PreviewWrapper,
  StyledDragger,
  StyledUploadWrapper,
} from './FileUpload.styled';

export interface FileData {
  url?: string;
  image?: string;
  name?: string;
  size?: number | string;
}

interface FileUploadProps {
  value?: FileData;
  onChange?: (fileData: FileData | string | null) => void;
  accept?: string;
  maxSizeInMB?: number;
  minHeight?: number;
  minWidth?: number;
  label?: string;
  description?: string;
  onRemoveAsset?: () => void;
  onClick?: () => void;
  isMobile?: boolean;
  openFileDialogOnClick?: boolean;
  mode?: 'dynamic' | 'static';
  isLoading?: boolean;
  onValidateFile?: (fileData: FileData) => boolean;
}

export const FileUpload = ({
  value,
  onChange,
  accept = '.jpg,.jpeg,.png,.svg',
  maxSizeInMB = 1,
  minHeight = 48,
  minWidth,
  label = 'Click here or drag file to upload',
  description,
  onRemoveAsset,
  onClick,
  isMobile,
  openFileDialogOnClick = true,
  mode,
  isLoading = false,
  onValidateFile,
}: FileUploadProps) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [textWidth, setTextWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      setFileName('');
      return;
    }
    setPreview(sanitizeUrl(value.url || ''));
    setFileName(sanitizeFileName(value.name || ''));
  }, [value]);

  useLayoutEffect(() => {
    if (!preview || !containerRef.current) return;

    const calculateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const buttonSpace = 80;
        const imageSpace = 72;
        const availableWidth = containerWidth - buttonSpace - imageSpace;
        setTextWidth(Math.max(availableWidth, 100));
      }
    };

    calculateWidth();

    const resizeObserver = new ResizeObserver(calculateWidth);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [preview]);

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isAccepted =
      !accept ||
      accept
        .split(',')
        .some(
          (type) =>
            file.type.startsWith(type.replace('.', 'image/').replace('jpg', 'jpeg')) || file.name.endsWith(type),
        );

    if (!isAccepted) {
      message.error('Unable to upload file.');
      return Upload.LIST_IGNORE;
    }

    const isLessThanMaxSize = file.size / 1024 / 1024 < maxSizeInMB;
    if (!isLessThanMaxSize) {
      message.error(`File must be smaller than ${maxSizeInMB}MB`);
      return Upload.LIST_IGNORE;
    }

    setFileName(sanitizeFileName(file.name));

    const processImage = (base64: string) => {
      const fileData = {
        url: base64,
        image: base64,
        name: sanitizeFileName(file.name),
        size: file.size,
      };
      if (onValidateFile && !onValidateFile(fileData)) {
        return;
      }
      setPreview(base64);
      onChange?.(fileData);
    };

    if (file.type.startsWith('image/') && (minWidth || minHeight)) {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        if ((minWidth && img.width < minWidth) || (minHeight && img.height < minHeight)) {
          message.error(`Image must be at least ${minWidth ? `${minWidth}px width and ` : ''}${minHeight}px height`);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => processImage(e.target?.result as string);
        reader.readAsDataURL(file);
      };

      img.src = objectUrl;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => processImage(e.target?.result as string);
      reader.readAsDataURL(file);
    }
    if (mode === 'static') {
      message.success('Uploaded file.');
    }
    return false;
  };

  const handleRemove = () => {
    if (typeof onRemoveAsset === 'function') {
      onRemoveAsset();
    } else {
      setFileList([]);
      setPreview(null);
      setFileName('');
      onChange?.(null);
    }
  };

  const handleCopy = () => {
    if (!preview) return;

    navigator.clipboard
      .writeText(preview)
      .then(() => message.success('File copied to clipboard'))
      .catch(() => message.error('Failed to copy file'));
  };

  const handleDraggerClick = (e: React.MouseEvent) => {
    if (isMobile && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const renderPreview = () => {
    if (!preview && !isLoading) return null;

    const isImage =
      preview &&
      (preview.startsWith('data:image/') ||
        preview.startsWith('http') ||
        /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(preview));
    const displayName = sanitizeFileName(fileName) || (isImage ? 'Image' : 'File');

    return (
      <StyledUploadWrapper>
        <PreviewContainer align="center" ref={containerRef}>
          <PreviewImageContainer>
            {isLoading ? (
              <LoadingWrapper>
                <Spin indicator={<LoadingOutlined spin />} size="large" />
              </LoadingWrapper>
            ) : (
              <PreviewImage $hasImage={!!isImage} $preview={preview || undefined}>
                {!isImage && <InboxIcon />}
              </PreviewImage>
            )}
          </PreviewImageContainer>
          <FileNameTextWrapper>
            <FileNameTextContent>
              <DisplayNameWrapper $textWidth={textWidth} title={displayName}>
                {displayName}
              </DisplayNameWrapper>
            </FileNameTextContent>
            <ButtonsWrapper>
              <PreviewButton data-testid="file-upload-delete-button" icon={<DeleteOutlined />} onClick={handleRemove} />
              <PreviewButton data-testid="file-upload-copy-button" icon={<CopyOutlined />} onClick={handleCopy} />
            </ButtonsWrapper>
          </FileNameTextWrapper>
        </PreviewContainer>
        <FileDescription data-testid="file-description">{description}</FileDescription>
      </StyledUploadWrapper>
    );
  };

  const uploadProps: UploadProps = {
    beforeUpload: handleBeforeUpload,
    fileList,
    showUploadList: false,
    multiple: false,
    openFileDialogOnClick,
  };

  return (
    <StyledUploadWrapper data-testid="file-upload-wrapper">
      {preview || isLoading ? (
        <FilePreview>{renderPreview()}</FilePreview>
      ) : (
        <>
          <PreviewContentWrapper>
            {isMobile ? (
              <PreviewWrapper onClick={handleDraggerClick}>
                <StyledDragger {...uploadProps} data-testid="file-upload-dragger">
                  <PreviewContent>
                    <IconWrapper>
                      <InboxIcon />
                    </IconWrapper>
                    <Typography.Text>{label}</Typography.Text>
                  </PreviewContent>
                </StyledDragger>
              </PreviewWrapper>
            ) : (
              <StyledDragger {...uploadProps} data-testid="file-upload-dragger">
                <PreviewContent>
                  <IconWrapper>
                    <InboxIcon />
                  </IconWrapper>
                  <Typography.Text>{label}</Typography.Text>
                </PreviewContent>
              </StyledDragger>
            )}
          </PreviewContentWrapper>
          <FileDescription>{description}</FileDescription>
        </>
      )}
    </StyledUploadWrapper>
  );
};
